<script setup lang="ts">
import type { InverterSubmission } from './types/inverterSubmission'
import { SolAlert } from '@solfacil/girassol'
import useFinancing from '~/store-v2/financings/useFinancing'
import useInstallation from '~/store-v2/installation/useInstallation'
import InverterDataConfirmation from './components/InverterDataConfirmation.vue'
import InverterManualMode from './components/InverterManualMode.vue'
import useInstallationAlerts, { buildInstallationAlertParameters, type InstallationErrorStatus } from './composables/useInstallationAlerts'
import { getPortalNameByBrand } from './schemas/portals'
import { useInstallationCommissioningFlowInject } from './useInstallationCommissioningFlow'
import useInstallationFlow from './useInstallationFlow'

// ===========================
// COMPOSABLES & SETUP
// ===========================

const { t } = useI18n()
const route = useRoute<any>()

const projectId = computed(() => route.params.id as string)

// ===========================
// REACTIVE STATE
// ===========================

const submission = ref<InverterSubmission | null>(null)
const formDraft = ref<InverterSubmission | null>(null)

// ===========================
// COMPOSABLES
// ===========================

const {
  currentAlert,
  hideInstallationAlert,
  showInstallationAlert,
} = useInstallationAlerts()

const commissioningFlow = useInstallationCommissioningFlowInject()

const {
  isLoading: isLoadingFinancing,
} = useFinancing(projectId, false)

const { errorState, clearLastError } = useInstallationFlow(projectId)

const { data: installation } = useInstallation(projectId)

const installationId = computed(() => installation.value?.id)

// ===========================
// COMPUTED PROPERTIES
// ===========================

const isLoading = computed(() => isLoadingFinancing.value)

// ===========================
// HELPER FUNCTIONS
// ===========================

function handleManualSubmit(payload: InverterSubmission): void {
  hideInstallationAlert()
  clearLastError()
  formDraft.value = null
  submission.value = payload
}

function handleConfirmationEdit(): void {
  hideInstallationAlert()
  formDraft.value = submission.value
  submission.value = null
}

watch(submission, (newValue) => {
  if (newValue) {
    hideInstallationAlert()
    clearLastError()
  }
})

watch(
  () => [
    commissioningFlow?.labelsAlert.value,
    commissioningFlow?.commissioningPayload.value?.data?.access_user,
    commissioningFlow?.commissioningPayload.value?.data?.monitorings,
  ] as const,
  ([status]) => {
    if (submission.value)
      return

    if (!status) {
      hideInstallationAlert()
      return
    }

    const monitorings = commissioningFlow?.commissioningPayload.value?.data?.monitorings
    const brand = monitorings?.[0]?.brand ?? ''
    const accessUser = commissioningFlow?.commissioningPayload.value?.data?.access_user

    showInstallationAlert(
      status as InstallationErrorStatus,
      buildInstallationAlertParameters(status as InstallationErrorStatus, {
        platformName: getPortalNameByBrand(brand),
        accessUser,
      }),
    )
  },
  { immediate: true, deep: true },
)
</script>

<template>
  <SolAlert
    v-if="currentAlert && !submission"
    id="commissioning-error-alert"
    feedback="error"
    :title="currentAlert.title"
    class="mb-4"
    @close="hideInstallationAlert"
  >
    <div>
      <template v-if="Array.isArray(currentAlert.text)">
        <template v-for="(line, index) in currentAlert.text" :key="index">
          <span>{{ line }}</span>
          <br v-if="index < (currentAlert.text.length || 0) - 1">
        </template>
      </template>
      <span v-else>{{ currentAlert.text }}</span>
    </div>
  </SolAlert>

  <SolAlert
    v-if="errorState && !submission"
    id="inverter-installation-error"
    feedback="error"
    :title="errorState.title"
    class="mb-4"
    @close="clearLastError"
  >
    <div>
      <template v-if="Array.isArray(errorState.text)">
        <template v-for="(line, index) in errorState.text" :key="index">
          <span>{{ line }}</span>
          <br v-if="index < (errorState.text.length || 0) - 1">
        </template>
      </template>
      <span v-else>{{ errorState.text }}</span>
    </div>
  </SolAlert>

  <div v-if="isLoading" class="container">
    <div class="my-4 card-container flex flex-col justify-between w-full">
      <span class="loader-project" />
    </div>
  </div>

  <div v-else class="inversor-installation">
    <template v-if="submission">
      <InverterDataConfirmation
        :submission="submission"
        :project-id="projectId"
        :installation-id="installationId"
        @edit="handleConfirmationEdit"
      />
    </template>

    <template v-else>
      <h1 class="page-title">
        {{ t('inversorInstallation.form.title') }}
      </h1>

      <SolDivider thickness="x-small" orientation="horizontal" />

      <div class="page-container">
        <div class="page-container-left">
          <figure class="w-full">
            <img
              class="w-full"
              src="/images/inversor_example.svg"
              :alt="t('inversorInstallation.form.inverterImageAlt')"
            >
          </figure>
        </div>

        <div class="page-container-right">
          <InverterManualMode
            :initial-submission="formDraft"
            @submit="handleManualSubmit"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.inversor-installation {
  @apply flex flex-col justify-center items-center gap-2;
  @apply w-full py-2xs px-4xs rounded-sm;
  @apply bg-neutral-high-pure;

  @screen md:site {
    @apply items-start;
    @apply py-xs px-2xs rounded-lg;
  }

  > .page-title {
    @apply font-highlight text-sm text-neutral-low-dark;
  }

  > .page-container {
    @apply grid grid-cols-1 gap-10;
    @apply py-2xs w-full;

    @screen md:site {
      @apply grid-cols-2;
      grid-template-columns: 300px 1fr;
    }

    > .page-container-left {
      @apply col-span-1;
    }

    > .page-container-right {
      @apply col-span-1;
    }
  }
}
</style>
